import type { ComponentType, ReactNode } from 'react'
import { IconChevronLeft } from '../../../shared/console'
import { KalqyMark } from './KalqyMark'
import { ShowcasePanel, type ShowcaseSceneId } from './showcase/ShowcasePanel'

/**
 * The school and teacher sign-in layout (Figma P3 "Sign in" 11:439, P4 "Set
 * your password" 11:551), split 50/50: on the left an inset picture panel with
 * the live pose demo (`ShowcasePanel`) and the page's headline and points; on
 * the right a slim header and the form, centred.
 *
 * ⚠️ THE FORM SIDE IS UNCHANGED BY THE SPLIT. Only the left panel and the
 * header moved: the product owner asked for the panel to show something —
 * "interactive, interesting" — and kept the right side as it was. The panel
 * shows the product's promise (the camera keeps seventeen points, nothing
 * else) rather than listing it.
 *
 * 🔴 NOT THE KALQY TEAM'S DOOR. The operator sign-in has its own page
 * (`OperatorAuthShell`) — the product owner's rule is that Kalqy's own login is
 * never the school login with a third tab.
 *
 * 🔴 IT WEARS `.console` ITSELF. The staff doors render outside every console
 * shell (AppRouter serves them bare), so the class that swaps the warm tokens
 * for the console ones has to be on this root — and only here, so the kid
 * sign-in and the A1 chooser keep their playful look.
 *
 * ⚠️ THE FORM COMES FIRST IN THE DOM; THE GRID PUTS THE PANEL ON THE LEFT. So
 * a keyboard reaches the email field before the demo's buttons.
 *
 * Below `lg` the panel is not rendered at all — just the mark above the form;
 * the points are context, not something a phone user needs before typing a
 * password, and a phone should not pay for the panel's picture.
 */

export interface AuthPoint {
  icon: ComponentType<{ size?: number; className?: string }>
  title: string
  body: string
}

/** The header's one link on the form side, e.g. "New to Kalqy? Contact sales". */
export interface AuthTopLink {
  lead?: string
  label: string
  href: string
}

export function AuthSplit({
  headline,
  points,
  children,
  testId,
  width = 'narrow',
  scene = 'signin',
  topLink = null,
}: {
  headline: string
  points: readonly AuthPoint[]
  children: ReactNode
  testId?: string
  /** 400px for sign-in (P3), 440px for set-password (P4). */
  width?: 'narrow' | 'wide'
  /** Which picture the left panel shows. */
  scene?: ShowcaseSceneId
  topLink?: AuthTopLink | null
}) {
  const year = new Date().getFullYear()
  return (
    <div data-testid={testId} className="console min-h-dvh bg-canvas font-dash text-ink lg:grid lg:grid-cols-2">
      <div className="flex min-h-dvh min-w-0 flex-col lg:col-start-2 lg:row-start-1">
        <header className="flex h-16 shrink-0 items-center justify-between gap-4 px-4 sm:px-8 lg:px-12">
          <span className="lg:hidden">
            <KalqyMark href="#/" />
          </span>
          <a
            href="#/"
            className="hidden items-center gap-1 rounded-md py-1 pr-1.5 text-[13px] text-ink-soft transition-colors duration-150 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink lg:inline-flex"
          >
            <IconChevronLeft size={16} />
            Back to website
          </a>
          {topLink ? (
            <p className="text-[13px] text-ink-soft">
              {topLink.lead ? <span className="hidden sm:inline">{topLink.lead} </span> : null}
              <a
                href={topLink.href}
                className="rounded-sm font-medium text-ink underline decoration-line-strong underline-offset-4 transition-colors duration-150 hover:decoration-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink"
              >
                {topLink.label}
              </a>
            </p>
          ) : null}
        </header>

        <main id="main" className="flex flex-1 items-start justify-center px-4 pt-4 pb-14 sm:px-8 lg:items-center lg:px-12 lg:py-10">
          <div className={`w-full ${width === 'wide' ? 'max-w-[440px]' : 'max-w-[400px]'}`}>{children}</div>
        </main>

        <p className="px-4 pb-6 text-center text-[12px] text-muted sm:px-8 lg:px-12 lg:text-left">
          © {year} Kalqy Global Private Limited
        </p>
      </div>

      <aside className="hidden lg:sticky lg:top-0 lg:col-start-1 lg:row-start-1 lg:block lg:h-dvh lg:p-3">
        <ShowcasePanel scene={scene}>
          <h1 className="text-[40px] leading-[44px] font-semibold tracking-[-0.03em] text-white">{headline}</h1>
          <ul className="mt-6 grid grid-cols-3 gap-5">
            {points.map(({ icon: Icon, title, body }) => (
              <li key={title} className="min-w-0">
                <span
                  aria-hidden="true"
                  className="inline-flex h-9 w-9 items-center justify-center rounded-[10px] bg-white/12 text-white ring-1 ring-white/20 backdrop-blur-sm"
                >
                  <Icon size={18} />
                </span>
                <span className="mt-3 block text-[13.5px] leading-5 font-medium text-white">{title}</span>
                <span className="mt-0.5 hidden text-[12.5px] leading-[18px] text-white/70 [@media(min-width:1280px)_and_(min-height:860px)]:block">
                  {body}
                </span>
              </li>
            ))}
          </ul>
        </ShowcasePanel>
      </aside>
    </div>
  )
}

/** The heading block every right-hand form starts with. */
export function AuthHeading({ title, subtitle }: { title: ReactNode; subtitle?: ReactNode }) {
  return (
    <div>
      <h2 className="text-[22px] leading-7 font-semibold tracking-[-0.02em] text-ink">{title}</h2>
      {subtitle ? <p className="mt-1 text-[13px] leading-5 text-ink-soft">{subtitle}</p> : null}
    </div>
  )
}

export type StaffDoor = 'school' | 'teacher' | 'operator'

const DOOR_LABEL: Record<StaffDoor, string> = {
  school: 'School admin',
  teacher: 'Teacher',
  operator: 'Kalqy team',
}

/**
 * Figma P3's segmented control: who is signing in.
 *
 * 🔴 IT NAVIGATES BETWEEN DOORS; IT ASSERTS NOTHING. Each door is its own route
 * that POSTs the same `/auth/login`, and the account still decides the role —
 * a mismatched account is signed back out by the router. Choosing a tab only
 * changes which page you are on, which is why these are links-in-spirit
 * (`aria-current`) rather than a value a form could submit.
 */
export function DoorTabs({
  current,
  doors,
  onDoor,
}: {
  current: StaffDoor
  doors: readonly StaffDoor[]
  onDoor: (door: StaffDoor) => void
}) {
  return (
    <nav aria-label="Who is signing in" className="mt-6">
      <ul
        className="grid gap-1 rounded-[10px] bg-surface-muted p-1"
        style={{ gridTemplateColumns: `repeat(${String(doors.length)}, minmax(0, 1fr))` }}
      >
        {doors.map((d) => {
          const active = d === current
          return (
            <li key={d}>
              <button
                type="button"
                aria-current={active ? 'page' : undefined}
                data-testid={`door-${d}`}
                onClick={() => {
                  if (!active) onDoor(d)
                }}
                className={`h-[30px] w-full rounded-[7px] px-2 text-[13px] leading-5 transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink ${
                  active
                    ? 'bg-surface font-medium text-ink shadow-[0_1px_2px_rgba(20,18,13,0.08)]'
                    : 'text-ink-soft hover:text-ink'
                }`}
              >
                {DOOR_LABEL[d]}
              </button>
            </li>
          )
        })}
      </ul>
    </nav>
  )
}
